import {ReactNode, useCallback, useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {toast} from 'sonner';
import {ArrowLeftIcon, Building2, Loader2} from 'lucide-react';
import {useCreateCampaign, usePatchCampaign} from '@tinct/core-api';
import {LandingPageOriginType, LandingPageVariationAxis} from '@tinct/core-api-models';
import {useCurrentWorkspaceUrlPrefix, useGetCurrentWorkspaceId} from '@tinct/core-workspace';
import {useConfig} from '@tinct/core-config';
import {useStaticAssetsUrl} from '@tinct/core-utils';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  GoogleAdsIcon,
  LinkedinAdsIcon,
} from '@tinct/web-components';

// Hash-driven like the paywall modal: any CTA can open the dialog in place
// with `<Link to={{hash: NEW_CAMPAIGN_DIALOG_HASH}}>` (or the hook below),
// and the dialog itself is mounted once per workspace layout.
export const NEW_CAMPAIGN_DIALOG_HASH = 'new-campaign';

export function useOpenNewCampaignDialog() {
  const navigate = useNavigate();

  const openNewCampaignDialog = useCallback(() => {
    navigate({hash: NEW_CAMPAIGN_DIALOG_HASH}, {replace: true});
  }, [navigate]);

  const closeNewCampaignDialog = useCallback(() => {
    navigate({hash: ''}, {replace: true});
  }, [navigate]);

  return {openNewCampaignDialog, closeNewCampaignDialog};
}

// A campaign kind is the axis (what variants are declined along) × the origin
// (where the landing page comes from). Both are set at creation.
type CampaignKind = {
  originType: LandingPageOriginType;
  axis: LandingPageVariationAxis;
};

const kindKey = ({axis, originType}: CampaignKind) => `${axis}:${originType}`;

// First user chat message auto-sent on the funnel's configure step. Arriving
// there without one bounces back here (see use-campaign-chat), so each kind
// must carry an opener; the URL of an existing page is collected by the chat
// agent, no longer by this dialog.
const PENDING_MESSAGES: Record<string, string> = {
  'company_list:new': `Setting up a campaign that starts from a blank page — a new page generated by Tinct, on-brand from day one.

First, let's confirm which site this campaign is for. Then we'll align on four things:
goal, pain point addressed, value proposition, and ICP / target, and I'll walk you through each one.`,
  'company_list:existing': `Setting up a campaign that personalizes one of my existing landing pages.

To personalize this page for incoming visitors, we need to align on four things:
goal, pain point addressed, value proposition, and ICP / target, and I'll walk you through each one.`,
  'adword:existing': `Setting up an ads campaign (Google Ads / LinkedIn Ads) that personalizes the landing page my ads point at — each matched ads campaign gets its own page variant following its search intent.

First, let's confirm the page URL your ads send visitors to. Then we'll align on four things:
goal, pain point addressed, value proposition, and ICP / target, and I'll walk you through each one.`,
  'adword:new': `Setting up an ads campaign (Google Ads / LinkedIn Ads) that starts from a blank page — a new page generated by Tinct that my ads will point at, with a variant per matched ads campaign following its search intent.

First, let's confirm which site this campaign is for. Then we'll align on four things:
goal, pain point addressed, value proposition, and ICP / target, and I'll walk you through each one.`,
};

type OriginCardProps = {
  icon: ReactNode;
  title: string;
  description: string;
  isPending: boolean;
  disabled: boolean;
  onSelect: () => void;
};

const OriginCard = ({icon, title, description, isPending, disabled, onSelect}: OriginCardProps) => (
  <button
    type="button"
    onClick={onSelect}
    disabled={disabled}
    className="flex aspect-square w-full flex-col items-center justify-center gap-4 rounded-2xl border border-input bg-background p-6 shadow-xs transition-colors hover:bg-accent disabled:pointer-events-none disabled:opacity-50 sm:size-[250px]"
  >
    {isPending ? (
      <Loader2 className="size-14 animate-spin text-muted-foreground" />
    ) : (
      icon
    )}
    <div className="flex flex-col gap-1 text-center">
      <span className="text-sm font-semibold text-foreground">{title}</span>
      <span className="text-xs text-muted-foreground">{description}</span>
    </div>
  </button>
);

export const NewCampaignDialog = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const assetsUrl = useStaticAssetsUrl();
  const workspaceId = useGetCurrentWorkspaceId();
  const workspaceUrlPrefix = useCurrentWorkspaceUrlPrefix();
  const {closeNewCampaignDialog} = useOpenNewCampaignDialog();

  const {mutateAsync: createCampaign} = useCreateCampaign();
  const {mutateAsync: patchCampaign} = usePatchCampaign();

  // Generation isn't live for everyone yet — fail closed until the backend
  // flag is enabled for the workspace.
  const {isFeatureEnabled} = useConfig();
  const generationEnabled = isFeatureEnabled('landingPageGeneration');
  // The ADWORD axis covers both ads providers: one flag is enough to offer it.
  const adsEnabled =
    isFeatureEnabled('googleAdsIntegration') || isFeatureEnabled('linkedinAdsIntegration');

  const [pendingKind, setPendingKind] = useState<string | null>(null);
  // Two-step choice: first the variation axis (what drives the variants),
  // then the landing-page origin (new or existing) for that axis. A step whose
  // alternatives are feature-flagged off is skipped entirely: its only
  // remaining choice is taken as selected (forcedAxis / forcedOrigin), and if
  // both steps collapse the dialog auto-creates the campaign on open.
  const [selectedAxis, setSelectedAxis] = useState<LandingPageVariationAxis | null>(null);

  const forcedAxis: LandingPageVariationAxis | null = adsEnabled ? null : 'company_list';
  const forcedOrigin: LandingPageOriginType | null = generationEnabled ? null : 'existing';
  const effectiveAxis = selectedAxis ?? forcedAxis;

  const isOpen = location.hash === `#${NEW_CAMPAIGN_DIALOG_HASH}`;

  const handleSelect = useCallback(
    async (kind: CampaignKind): Promise<boolean> => {
      if (!workspaceId || pendingKind) return false;
      setPendingKind(kindKey(kind));
      try {
        const campaign = await createCampaign({organization: {id: workspaceId}});
        if (!campaign.id) throw new Error('Campaign created without an id');
        await patchCampaign({
          id: campaign.id,
          data: {
            landingPageOriginType: kind.originType,
            landingPageVariationAxis: kind.axis,
          },
        });
        // Navigating away drops the hash, which closes the dialog.
        navigate(`${workspaceUrlPrefix}/funnel-campaigns/${campaign.id}/configure`, {
          state: {pendingMessage: PENDING_MESSAGES[kindKey(kind)]},
        });
        return true;
      } catch {
        toast.error('Could not create the campaign. Please try again.');
        return false;
      } finally {
        setPendingKind(null);
      }
    },
    [workspaceId, pendingKind, createCampaign, patchCampaign, navigate, workspaceUrlPrefix],
  );

  // Both steps collapsed to their only choice: create right away instead of
  // showing a dialog with a single card. One attempt per open — a failure
  // closes the dialog (the toast carries the error) rather than looping.
  const autoSubmittedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      autoSubmittedRef.current = false;
      return;
    }
    if (!forcedAxis || !forcedOrigin || !workspaceId || autoSubmittedRef.current) return;
    autoSubmittedRef.current = true;
    void handleSelect({axis: forcedAxis, originType: forcedOrigin}).then((created) => {
      if (!created) closeNewCampaignDialog();
    });
  }, [isOpen, forcedAxis, forcedOrigin, workspaceId, handleSelect, closeNewCampaignDialog]);

  const cardProps = (kind: CampaignKind) => ({
    isPending: pendingKind === kindKey(kind),
    disabled: pendingKind !== null,
    onSelect: () => handleSelect(kind),
  });

  // Both steps collapsed: there is no choice to present, and the effect above
  // is already creating the campaign — rendering the dialog would only flash
  // a loader for the instant before the funnel redirect.
  if (forcedAxis && forcedOrigin) return null;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          closeNewCampaignDialog();
          setSelectedAxis(null);
        }
      }}
    >
      <DialogContent className="gap-6 p-8 sm:max-w-[576px]">
        {effectiveAxis === null ? (
          <>
            <DialogHeader>
              <DialogTitle>Let&apos;s create your campaign</DialogTitle>
              <DialogDescription>
                What should drive your page variants?
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3 sm:flex-row">
              {/* When the origin step is collapsed, picking an axis is the
                  last choice — it creates the campaign directly. */}
              <OriginCard
                icon={<Building2 className="size-14 text-muted-foreground" strokeWidth={1.25} />}
                title="Company list campaign"
                description="A page variant per targeted company — the page follows who the visitor works for."
                {...(forcedOrigin
                  ? cardProps({originType: forcedOrigin, axis: 'company_list'})
                  : {
                      isPending: false,
                      disabled: pendingKind !== null,
                      onSelect: () => setSelectedAxis('company_list'),
                    })}
              />
              {adsEnabled && (
                <OriginCard
                  icon={
                    <span className="flex items-center gap-1">
                      <GoogleAdsIcon width={40} height={40} />
                      <LinkedinAdsIcon width={40} height={40} />
                    </span>
                  }
                  title="Ads campaign (Google Ads / LinkedIn Ads)"
                  description="A page variant per ads campaign, following the intent that brought the visitor."
                  {...(forcedOrigin
                    ? cardProps({originType: forcedOrigin, axis: 'adword'})
                    : {
                        isPending: false,
                        disabled: pendingKind !== null,
                        onSelect: () => setSelectedAxis('adword'),
                      })}
                />
              )}
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>
                {forcedAxis
                  ? "Let's create your campaign"
                  : effectiveAxis === 'adword'
                    ? 'Ads campaign (Google Ads / LinkedIn Ads)'
                    : 'Company list campaign'}
              </DialogTitle>
              <DialogDescription>
                {effectiveAxis === 'adword'
                  ? "Where should your ads' visitors land?"
                  : 'Pick a starting point — an existing page, or start from scratch.'}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3 sm:flex-row">
              <OriginCard
                icon={<img src={`${assetsUrl}/new-campaign-existing-page.png`} alt="" className="size-14 object-cover" />}
                title="Personalize an existing page"
                description={
                  effectiveAxis === 'adword'
                    ? 'Your ads already point at a landing page — each matched ads campaign gets its own variant of it.'
                    : 'Generate page variants of your landing page for each target account — same brand, tailored messaging.'
                }
                {...cardProps({originType: 'existing', axis: effectiveAxis})}
              />
              {generationEnabled && (
                <OriginCard
                  icon={<img src={`${assetsUrl}/new-campaign-blank-page.png`} alt="" className="size-14 object-cover" />}
                  title="Start from a blank page"
                  description={
                    effectiveAxis === 'adword'
                      ? 'Tinct builds the landing page your ads will point at, then declines it per ads campaign.'
                      : 'Launch a new campaign page, on-brand from day one — if needed, turn it into targeted page variants later.'
                  }
                  {...cardProps({originType: 'new', axis: effectiveAxis})}
                />
              )}
            </div>
            {!forcedAxis && (
              <Button
                variant="ghost"
                size="sm"
                className="self-start"
                disabled={pendingKind !== null}
                onClick={() => setSelectedAxis(null)}
              >
                <ArrowLeftIcon className="size-4" />
                Back
              </Button>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
